import { Typography } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import { Alert, Autocomplete, Box, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import UiSideModal from "components/base/UiSideModal";
import UiButton from "components/base/UiButton";
import { CostRow, Custom, materialUnits, money } from "./calculator";
import { plantMaterialRow } from "./library";
import CustomMaterialQuantity, { materialQuantityPreview } from "./CustomMaterialQuantity";

// Match Add Product's outlined selectors, with room for material names and rates.
const materialSelectorSx = {
  "& .MuiOutlinedInput-root": { minHeight: 44, borderRadius: "8px", fontSize: 13 },
  "& .MuiOutlinedInput-input": { padding: "11px 12px" },
  "& .MuiSelect-select.MuiSelect-select": {
    padding: "11px 36px 11px 12px",
    minHeight: "20px",
    lineHeight: "20px",
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
    color: "#7B809A",
    top: 0,
    lineHeight: 1.2,
    transform: "translate(12px, -7px) scale(0.85)",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E3E8" },
  "& .MuiFormHelperText-root": { marginTop: "6px", marginLeft: 0, fontSize: 12 },
};
const materialMenuSx = {
  maxHeight: 320,
  "& .MuiMenuItem-root": {
    minHeight: 40,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
};

const PLANTS = gql`
  query CustomPlantLibrary($search: String, $page: Int!) {
    getPlants(search: $search, first: 50, page: $page) {
      data {
        id
        name
        products {
          id
          type
          rate
          plantId
        }
      }
      paginatorInfo {
        currentPage
        hasMorePages
      }
    }
  }
`;
type Plant = {
  id: string;
  name: string;
  products: { id: string; type: string; rate: number; plantId: string }[];
};
export default function CustomMaterialLibraryDrawer({
  initialRow,
  item,
  settings,
  disabled,
  onClose,
  onAdd,
}: {
  initialRow?: CostRow;
  item: Custom;
  settings: any;
  disabled: boolean;
  onClose: () => void;
  onAdd: (row: CostRow) => void;
}) {
  const [search, setSearch] = useState("");
  const [plantInput, setPlantInput] = useState(initialRow?.sourcePlantName ?? "");
  const [plantMenuOpen, setPlantMenuOpen] = useState(false);
  const latestSearch = useRef(search);
  latestSearch.current = search;
  const [debounced, setDebounced] = useState("");
  const loadingMore = useRef(false);
  const [morePending, setMorePending] = useState(false);
  const [moreError, setMoreError] = useState("");
  const [plantId, setPlantId] = useState(initialRow?.sourcePlantId ?? "");
  const [selectedPlant, setSelectedPlant] = useState<Plant | null>(
    initialRow
      ? {
          id: initialRow.sourcePlantId!,
          name: initialRow.sourcePlantName ?? "Saved plant",
          products: [],
        }
      : null
  );
  const [baseRow, setBaseRow] = useState<CostRow | null>(initialRow ?? null);
  const [productId, setProductId] = useState(initialRow?.sourceId ?? "");
  const [configuration, setConfiguration] = useState<Partial<CostRow>>({});
  const [unit, setUnit] = useState(initialRow?.unit ?? "");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setMoreError("");
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, loading, error, refetch, fetchMore } = useQuery(PLANTS, {
    variables: { search: debounced, page: 1 },
    fetchPolicy: "network-only",
  });
  const loadMore = async () => {
    if (
      loadingMore.current ||
      loading ||
      !data?.getPlants?.paginatorInfo.hasMorePages ||
      debounced !== search
    )
      return;
    const requestedSearch = debounced;
    loadingMore.current = true;
    setMorePending(true);
    setMoreError("");
    try {
      await fetchMore({
        variables: { page: data.getPlants.paginatorInfo.currentPage + 1 },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult || latestSearch.current !== requestedSearch) return previous;
          const rows = [...previous.getPlants.data, ...fetchMoreResult.getPlants.data];
          return {
            ...previous,
            getPlants: {
              ...fetchMoreResult.getPlants,
              data: rows.filter(
                (p, i) => rows.findIndex((candidate) => candidate.id === p.id) === i
              ),
            },
          };
        },
      });
    } catch (e) {
      if (latestSearch.current === requestedSearch) setMoreError((e as Error).message);
    } finally {
      loadingMore.current = false;
      setMorePending(false);
    }
  };
  const pending = loading || debounced !== search;
  const plants: Plant[] = data?.getPlants?.data ?? [];
  const plant = plants.find((p) => p.id === plantId) ?? selectedPlant;
  const plantOptions =
    selectedPlant && !plants.some((p) => p.id === selectedPlant.id)
      ? [selectedPlant, ...plants]
      : plants;
  const products = plant?.products ?? [];
  const savedProduct =
    baseRow && baseRow.sourcePlantId === plantId && !products.some((p) => p.id === baseRow.sourceId)
      ? {
          id: baseRow.sourceId!,
          type: baseRow.sourceName ?? baseRow.name,
          rate: Number(baseRow.rate) * 100,
          plantId,
        }
      : null;
  const productOptions = savedProduct ? [savedProduct, ...products] : products;
  const product = productOptions.find((p) => p.id === productId);
  const draft = baseRow && product && unit ? { ...baseRow, ...configuration, unit } : null;
  const quantityError = draft ? materialQuantityPreview(item, draft).error : "";
  return (
    <UiSideModal
      open
      onClose={onClose}
      title={initialRow ? "Calculate material quantity" : "Add Material from Plant"}
      width="598px"
    >
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography sx={{ fontSize: 13, whiteSpace: "normal" }}>
          Select a plant material, then use the job size and coverage or weight settings to
          calculate the quantity you need.
        </Typography>
        {error && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={() => refetch()}>
                Retry
              </UiButton>
            }
          >
            {error.message}
          </Alert>
        )}
        <Autocomplete
          fullWidth
          size="small"
          open={plantMenuOpen}
          onOpen={() => setPlantMenuOpen(true)}
          onClose={() => {
            setPlantMenuOpen(false);
            setPlantInput(plant?.name ?? "");
            setSearch("");
          }}
          disableCloseOnSelect
          disabled={disabled}
          value={plant ?? null}
          inputValue={plantInput}
          options={[
            ...plantOptions,
            ...(data?.getPlants?.paginatorInfo.hasMorePages
              ? [
                  {
                    id: "__load_more",
                    name: morePending ? "Loading plants…" : "Load more plants",
                    products: [],
                  },
                ]
              : []),
          ]}
          filterOptions={(options) => options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionDisabled={(option) => option.id === "__load_more" && (pending || morePending)}
          loading={pending || morePending}
          loadingText="Loading plants…"
          noOptionsText={error ? "Unable to load plants" : "No matching plants"}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              setPlantInput(value);
              setSearch(value);
            }
          }}
          onChange={(_, next) => {
            if (next?.id === "__load_more") {
              void loadMore();
              return;
            }
            if (next?.id !== plantId) setProductId("");
            setPlantId(next?.id ?? "");
            setSelectedPlant(next);
            setPlantInput(next?.name ?? "");
            setSearch("");
            setPlantMenuOpen(false);
          }}
          ListboxProps={{
            style: { maxHeight: 320 },
            onScroll: (event) => {
              const list = event.currentTarget;
              if (list.scrollHeight - list.scrollTop - list.clientHeight < 80) void loadMore();
            },
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}
              style={{
                minHeight: 40,
                padding: "10px 12px",
                fontSize: 13,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={materialSelectorSx}
              variant="outlined"
              label="Select Plant"
              placeholder="Search plants"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(pending || morePending) && (
                      <CircularProgress size={18} aria-label="Loading plants" />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {moreError && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={loadMore}>
                Retry
              </UiButton>
            }
          >
            {moreError}
          </Alert>
        )}
        <TextField
          sx={materialSelectorSx}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          fullWidth
          select
          size="small"
          label="Plant Product"
          SelectProps={{ MenuProps: { PaperProps: { sx: materialMenuSx } } }}
          value={product?.id ?? ""}
          disabled={!plant || pending || disabled}
          onChange={(e) => {
            setProductId(e.target.value);
            const next = productOptions.find((p) => p.id === e.target.value);
            if (plant && next) {
              const source = plantMaterialRow(plant, next, unit);
              setBaseRow((current) => ({
                ...current,
                ...source,
                quantity: current?.quantity ?? "1",
                calculation: current?.calculation ?? "manual",
              }));
            }
          }}
        >
          {productOptions.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.type} — {money(p.rate)}
              {unit ? ` per ${unit}` : ""}
            </MenuItem>
          ))}
        </TextField>
        {!initialRow && (
          <TextField
            sx={materialSelectorSx}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            fullWidth
            select
            required
            size="small"
            label="Unit"
            SelectProps={{ MenuProps: { PaperProps: { sx: materialMenuSx } } }}
            value={unit}
            disabled={disabled}
            onChange={(e) => setUnit(e.target.value)}
            helperText="Choose the unit this plant price purchases."
          >
            {materialUnits.map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
        )}
        {draft && (
          <CustomMaterialQuantity
            item={item}
            row={draft}
            settings={settings}
            busy={disabled}
            readOnly={false}
            onChange={(patch) => {
              // A helper must not reinterpret the plant rate in another purchasing unit.
              // New rows choose Unit above; existing rows use their material-row Unit.
              const { unit: changedUnit, ...rest } = patch;
              setConfiguration((current) => ({ ...current, ...rest }));
            }}
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {initialRow && (
            <UiButton uiVariant="outlined-small" onClick={onClose}>
              Cancel
            </UiButton>
          )}
          <UiButton
            uiVariant="primary-green"
            disabled={!draft || pending || disabled || !!error || !!quantityError}
            onClick={() => {
              if (draft && !quantityError) {
                onAdd(draft);
                onClose();
              }
            }}
          >
            {initialRow ? "Apply" : "Add"}
          </UiButton>
        </Box>
      </Box>
    </UiSideModal>
  );
}
